/**
 * Makes the collection Size facet inventory-aware.
 *
 * Shopify's native option filter matches products that define a size even when
 * every variant in that size is unavailable. Collection product JSON exposes
 * live variant availability, so this layer keeps sold-out choices visible,
 * reports in-stock counts, and removes sold-out matches from the rendered grid.
 */
(() => {
  const collectionCache = new Map();
  const contextHandleCache = new Map();
  const PAGE_SIZE = 250;
  const MAX_PAGES = 20;
  const SIZE_PARAM = "filter.v.option.size";
  let refreshSequence = 0;
  let isAutoFilling = false;

  const normalize = (value) => String(value || "").trim().toLowerCase();
  const canonicalOptionName = (value) => normalize(value).replace(/[^a-z0-9]+/g, "");

  function getSizeOption(product) {
    return (product.options || []).find(
      (option) => normalize(option.name) === "size"
    );
  }

  function getVariantOptionFilters(product, params) {
    const optionPositions = new Map(
      (product.options || []).map((option) => [
        canonicalOptionName(option.name),
        option.position
      ])
    );
    const optionFilters = new Map();
    let hasUnmatchedOption = false;

    params?.forEach((value, name) => {
      if (!name.startsWith("filter.v.option.") || name === SIZE_PARAM) return;

      const optionName = canonicalOptionName(name.replace("filter.v.option.", ""));
      const position = optionPositions.get(optionName);
      if (!position) {
        hasUnmatchedOption = true;
        return;
      }

      if (!optionFilters.has(position)) optionFilters.set(position, new Set());
      String(value).split(",").forEach((optionValue) => {
        optionFilters.get(position).add(normalize(optionValue));
      });
    });

    return { hasUnmatchedOption, optionFilters };
  }

  function variantMatchesContext(variant, params, optionContext) {
    if (optionContext.hasUnmatchedOption) return false;

    const { optionFilters } = optionContext;
    for (const [position, acceptedValues] of optionFilters) {
      if (!acceptedValues.has(normalize(variant[`option${position}`]))) return false;
    }

    const minimumPrice = Number.parseFloat(params?.get("filter.v.price.gte"));
    const maximumPrice = Number.parseFloat(params?.get("filter.v.price.lte"));
    const variantPrice = Number.parseFloat(variant.price);

    if (Number.isFinite(minimumPrice) && variantPrice < minimumPrice) return false;
    if (Number.isFinite(maximumPrice) && variantPrice > maximumPrice) return false;

    return true;
  }

  function getAvailableSizeProducts(products, allowedHandles = null, params = null) {
    const productHandlesBySize = new Map();

    products.forEach((product) => {
      if (allowedHandles && !allowedHandles.has(product.handle)) return;

      const sizeOption = getSizeOption(product);
      if (!sizeOption) return;

      const optionKey = `option${sizeOption.position}`;
      const availableSizes = new Set();
      const optionContext = getVariantOptionFilters(product, params);

      (product.variants || []).forEach((variant) => {
        if (
          !variant.available ||
          !variant[optionKey] ||
          !variantMatchesContext(variant, params, optionContext)
        ) return;
        availableSizes.add(normalize(variant[optionKey]));
      });

      availableSizes.forEach((size) => {
        if (!productHandlesBySize.has(size)) {
          productHandlesBySize.set(size, new Set());
        }

        productHandlesBySize.get(size).add(product.handle);
      });
    });

    return productHandlesBySize;
  }

  async function fetchCollectionProducts(collectionUrl) {
    const endpoint = `${collectionUrl.replace(/\/$/, "")}/products.json`;

    if (!collectionCache.has(endpoint)) {
      collectionCache.set(endpoint, (async () => {
        const products = [];

        for (let page = 1; page <= MAX_PAGES; page += 1) {
          const url = new URL(endpoint, window.location.origin);
          url.searchParams.set("limit", PAGE_SIZE);
          url.searchParams.set("page", page);

          const response = await fetch(url.toString(), {
            headers: { Accept: "application/json" }
          });

          if (!response.ok) {
            throw new Error(`Collection inventory request failed (${response.status})`);
          }

          const payload = await response.json();
          const pageProducts = Array.isArray(payload.products) ? payload.products : [];
          products.push(...pageProducts);

          if (pageProducts.length < PAGE_SIZE) break;
        }

        return products;
      })());
    }

    return collectionCache.get(endpoint);
  }

  function getContextSearchParams() {
    const params = new URLSearchParams(window.location.search);
    params.delete("page");
    params.delete("section_id");
    params.delete(SIZE_PARAM);
    return params;
  }

  function hasServerContextFilters(params) {
    return [...params.keys()].some(
      (name) =>
        name.startsWith("filter.") &&
        !name.startsWith("filter.v.option.") &&
        name !== "filter.v.price.gte" &&
        name !== "filter.v.price.lte"
    );
  }

  function getProductHandles(root) {
    return new Set(
      [...root.querySelectorAll("[data-product-handle]")]
        .map((card) => card.dataset.productHandle)
        .filter(Boolean)
    );
  }

  async function fetchContextHandles(form, params) {
    if (!hasServerContextFilters(params)) return null;

    const collectionUrl = form.dataset.collectionUrl;
    const sectionId = form.dataset.filterSectionId;
    const cacheKey = `${collectionUrl}?${params.toString()}`;

    if (!contextHandleCache.has(cacheKey)) {
      contextHandleCache.set(cacheKey, (async () => {
        const fetchPage = async (page) => {
          const url = new URL(collectionUrl, window.location.origin);
          params.forEach((value, name) => url.searchParams.append(name, value));
          url.searchParams.set("page", page);
          url.searchParams.set("section_id", sectionId);

          const response = await fetch(url.toString(), {
            headers: { Accept: "text/html" }
          });

          if (!response.ok) {
            throw new Error(`Filtered collection request failed (${response.status})`);
          }

          const template = document.createElement("template");
          template.innerHTML = await response.text();
          return template.content;
        };

        const firstPage = await fetchPage(1);
        const resultMeta = firstPage.querySelector("[data-num-results]");
        const resultCount = Number.parseInt(resultMeta?.dataset.numResults || "0", 10);
        const productsPerPage = Number.parseInt(
          resultMeta?.dataset.productsPerPage || "24",
          10
        );
        const pageCount = Math.min(
          MAX_PAGES,
          Math.max(1, Math.ceil(resultCount / productsPerPage))
        );
        const handles = getProductHandles(firstPage);

        if (pageCount > 1) {
          const remainingPages = await Promise.all(
            Array.from({ length: pageCount - 1 }, (_, index) => fetchPage(index + 2))
          );

          remainingPages.forEach((page) => {
            getProductHandles(page).forEach((handle) => handles.add(handle));
          });
        }

        return handles;
      })());
    }

    return contextHandleCache.get(cacheKey);
  }

  function setSizeFacetBusy(sizeFacet, busy) {
    if (!sizeFacet) return;

    const content = sizeFacet.querySelector(".filter__content");
    sizeFacet.toggleAttribute("aria-busy", busy);
    content?.toggleAttribute("inert", busy);
  }

  function updateSizeFacet(form, productHandlesBySize) {
    const sizeFacet = form.querySelector('[data-filter-label="size"]');
    if (!sizeFacet) return;

    const resultMeta = document.querySelector("[data-sold-out-label]");
    const soldOutLabel = resultMeta?.dataset.soldOutLabel || "Sold out";

    sizeFacet.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      const count = productHandlesBySize.get(normalize(input.value))?.size || 0;
      const label = input.labels && input.labels[0];
      const countElement = label && label.querySelector(".filter__count");
      const unavailable = count === 0;

      if (countElement) {
        countElement.textContent = unavailable
          ? soldOutLabel
          : `(${count})`;
      }

      input.dataset.availableProductCount = count;
      if (!input.checked) input.disabled = unavailable;
      input.closest("li")?.classList.toggle("filter-option--unavailable", unavailable);
    });

    setSizeFacetBusy(sizeFacet, false);
  }

  function selectedSizes(form) {
    return new Set(
      [...form.querySelectorAll(`[name="${SIZE_PARAM}"]:checked`)].map((input) =>
        normalize(input.value)
      )
    );
  }

  function eligibleHandlesForSizes(productHandlesBySize, sizes) {
    const eligibleHandles = new Set();

    sizes.forEach((size) => {
      productHandlesBySize.get(size)?.forEach((handle) => eligibleHandles.add(handle));
    });

    return eligibleHandles;
  }

  function updateResultCount(total) {
    const count = Number.isFinite(total) ? total : 0;
    const bannerCount = document.querySelector("[data-collection-result-count]");
    const drawerButton = document.querySelector(".js-close-drawer-mob");
    const resultMeta = document.querySelector("[data-result-label-one]");
    const resultLabelTemplate = count === 1
      ? resultMeta?.dataset.resultLabelOne
      : resultMeta?.dataset.resultLabelOther;
    const drawerLabelTemplate = count === 1
      ? resultMeta?.dataset.showResultsOne
      : resultMeta?.dataset.showResultsOther;
    const resultLabel = (resultLabelTemplate || `${count} products`).replace(
      "[count]",
      count
    );
    const drawerLabel = (drawerLabelTemplate || `Show ${count} results`).replace(
      "[count]",
      count
    );

    if (bannerCount) bannerCount.textContent = `(${resultLabel})`;
    if (drawerButton) drawerButton.textContent = drawerLabel;
  }

  function updatePagination(resultContainer, total) {
    const visibleProducts = resultContainer.querySelectorAll(
      "[data-product-handle]:not([hidden])"
    ).length;
    const pagination = resultContainer.querySelector(".js-pagination");
    const message = pagination?.querySelector(".js-pagination-message");
    const progress = pagination?.querySelector(".js-pagination-bar");

    if (message) {
      const resultMeta = document.querySelector("[data-pagination-label]");
      message.textContent = (
        resultMeta?.dataset.paginationLabel || "Showing [visible] of [total]"
      )
        .replace("[visible]", visibleProducts)
        .replace("[total]", total);
    }
    if (progress) {
      const percent = total > 0 ? Math.min(100, (visibleProducts / total) * 100) : 0;
      progress.style.setProperty("--pagination-percent", `${percent.toFixed(1)}%`);
    }

    if (pagination) pagination.hidden = visibleProducts >= total;
  }

  function updateEmptyState(resultContainer, total, hasSelectedSizes) {
    let emptyState = resultContainer.querySelector("[data-size-filter-empty]");

    if (hasSelectedSizes && total === 0) {
      if (!emptyState) {
        emptyState = document.createElement("p");
        emptyState.dataset.sizeFilterEmpty = "";
        emptyState.className = "size-filter-empty";
        emptyState.setAttribute("role", "status");
        const resultMeta = document.querySelector("[data-size-filter-empty-label]");
        emptyState.textContent = resultMeta?.dataset.sizeFilterEmptyLabel || "Size sold out.";
        resultContainer.prepend(emptyState);
      }
    } else {
      emptyState?.remove();
    }
  }

  function filterRenderedProducts(form, productHandlesBySize) {
    const resultContainer = document.getElementById("filter-results");
    if (!resultContainer) return null;

    const sizes = selectedSizes(form);
    const hasSelectedSizes = sizes.size > 0;
    const eligibleHandles = eligibleHandlesForSizes(productHandlesBySize, sizes);

    resultContainer.querySelectorAll("[data-product-handle]").forEach((card) => {
      const hidden = hasSelectedSizes && !eligibleHandles.has(card.dataset.productHandle);
      card.hidden = hidden;
      card.classList.toggle("size-filter-hidden", hidden);
    });

    if (hasSelectedSizes) {
      updateResultCount(eligibleHandles.size);
      updatePagination(resultContainer, eligibleHandles.size);
    }

    updateEmptyState(resultContainer, eligibleHandles.size, hasSelectedSizes);

    return {
      eligibleTotal: eligibleHandles.size,
      hasSelectedSizes,
      resultContainer,
      visibleProducts: resultContainer.querySelectorAll(
        "[data-product-handle]:not([hidden])"
      ).length
    };
  }

  async function fillFilteredPage(form, productHandlesBySize, sequence) {
    let state = filterRenderedProducts(form, productHandlesBySize);
    if (!state?.hasSelectedSizes || state.eligibleTotal === 0) return;

    const resultMeta = document.querySelector("[data-products-per-page]");
    const productsPerPage = Number.parseInt(
      resultMeta?.dataset.productsPerPage || "24",
      10
    );
    const target = Math.min(state.eligibleTotal, productsPerPage);
    const customPagination = document.querySelector("custom-pagination");

    if (
      state.visibleProducts >= target ||
      !customPagination ||
      typeof customPagination.loadMore !== "function"
    ) return;

    isAutoFilling = true;

    try {
      for (let page = 2; page <= MAX_PAGES && state.visibleProducts < target; page += 1) {
        const pagination = customPagination.querySelector(".js-pagination");
        const loadMoreButton = customPagination.querySelector(".js-pagination-load-more");
        if (
          pagination?.dataset.isMoreResults !== "true" ||
          !loadMoreButton ||
          customPagination.dataset.pauseInfiniteScroll === "true"
        ) break;

        const previousResultCount = customPagination.querySelectorAll(
          ".js-pagination-result"
        ).length;
        const previousHref = loadMoreButton.href;
        const loaded = await customPagination.loadMore();
        const nextResultCount = customPagination.querySelectorAll(
          ".js-pagination-result"
        ).length;
        const nextHref = customPagination.querySelector(
          ".js-pagination-load-more"
        )?.href;

        if (
          !loaded ||
          (nextResultCount <= previousResultCount && nextHref === previousHref)
        ) break;

        if (
          sequence !== refreshSequence ||
          form !== document.getElementById("facets")
        ) return;

        state = filterRenderedProducts(form, productHandlesBySize);
        if (!state) return;
      }
    } finally {
      isAutoFilling = false;

      if (
        sequence === refreshSequence &&
        form === document.getElementById("facets") &&
        customPagination.dataset.pauseInfiniteScroll !== "true"
      ) {
        filterRenderedProducts(form, productHandlesBySize);
      }
    }
  }

  async function refreshInStockSizeState() {
    const sequence = ++refreshSequence;
    const form = document.getElementById("facets");
    const collectionUrl = form?.dataset.collectionUrl;
    const sizeFacet = form?.querySelector('[data-filter-label="size"]');

    if (!form || !collectionUrl || !sizeFacet) return;

    setSizeFacetBusy(sizeFacet, true);

    try {
      const products = await fetchCollectionProducts(collectionUrl);
      const contextHandles = await fetchContextHandles(form, getContextSearchParams());

      if (sequence !== refreshSequence || form !== document.getElementById("facets")) return;

      const productHandlesBySize = getAvailableSizeProducts(
        products,
        contextHandles,
        getContextSearchParams()
      );
      updateSizeFacet(form, productHandlesBySize);
      await fillFilteredPage(form, productHandlesBySize, sequence);
    } catch (error) {
      setSizeFacetBusy(sizeFacet, false);
      console.warn("[DUXBUX] Could not refresh in-stock size filters.", error);
    }
  }

  function observeProductResults() {
    const resultContainer = document.getElementById("filter-results");
    if (!resultContainer || typeof MutationObserver === "undefined") return;

    const observer = new MutationObserver((mutations) => {
      const addedProductCards = mutations.some((mutation) =>
        [...mutation.addedNodes].some(
          (node) =>
            node.nodeType === 1 &&
            (node.matches?.("[data-product-handle]") ||
              node.querySelector?.("[data-product-handle]"))
        )
      );

      if (addedProductCards && !isAutoFilling) refreshInStockSizeState();
    });

    observer.observe(resultContainer, { childList: true, subtree: true });
  }

  const api = {
    eligibleHandlesForSizes,
    getAvailableSizeProducts,
    normalize
  };

  if (typeof module !== "undefined" && module.exports) module.exports = api;
  if (typeof document === "undefined") return;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      observeProductResults();
      refreshInStockSizeState();
    }, { once: true });
  } else {
    observeProductResults();
    refreshInStockSizeState();
  }

  document.addEventListener("on:facet-filters:updated", refreshInStockSizeState);
  document.addEventListener("shopify:section:load", refreshInStockSizeState);
})();
